<template>
  <div class="flex flex-col">
    <PageTitleHeader title="NFT Trades">
      <p class="flex flex-wrap items-center gap-x-1 text-base font-bold text-primary text-opacity-40">
        All NFTs are traded on
        <NuxtLink
          class="flex items-center gap-x-1 text-point-color"
          target="_blank"
          :to="MARKETPLACE_URL"
        >
          <img
            :src="`${IMAGE_BASE_URL}/logo/logo_mainnet_mobile.png`"
            alt="marketplace logo"
            width="16"
            height="16"
          >
          Maplestory Universe Marketplace
          <FontAwesomeIcon
            icon="external-link-square-alt"
            size="xs"
          />
        </NuxtLink>
        .
      </p>
    </PageTitleHeader>
    <DetailsBox class="mb-2 mt-4">
      Showing all <span class="text-primary">{{ data?.formattedTotalCount }}</span> results
      <template #pagination>
        <BcPagination
          v-model="pagination"
          :total-count="data?.viewCount"
          :row-per-page-list="rowPerPageList"
          hide-rows-per-page
        />
      </template>
    </DetailsBox>
    <Card>
      <CustomSuspense
        :is-loading="isLoading"
        :is-empty="isError || isEmpty(data?.tradeList)"
      >
        <template #loading>
          <LoadingTable
            :row-cnt="pagination.rowPerPage"
            :cols="column"
          />
        </template>
        <template #empty>
          <NoDataPreview />
        </template>
        <template #default>
          <BcTable
            :rows="data?.tradeList ?? []"
            :cols="column"
            @change-table-data="toggle"
          >
            <template #transactionHash="{row}: {row:NftTradeModel }">
              <TransactionCopyLink
                :hash="row.transactionHash"
                class="link"
              />
            </template>
            <template #sellerAddressInfo="{row}: {row: NftTradeModel}">
              <AddressCopyLink
                class="link"
                :address="row.sellerAddressInfo.address"
                :name="row.sellerAddressInfo.name"
                :type="row.sellerAddressInfo.addressType"
                hide-eoa-icon
              />
            </template>
            <template #arrow-right>
              <FontAwesomeIcon icon="arrow-right" />
            </template>
            <template #buyerAddressInfo="{row}: {row: NftTradeModel}">
              <AddressCopyLink
                class="link"
                :address="row.buyerAddressInfo.address"
                :name="row.buyerAddressInfo.name"
                :type="row.buyerAddressInfo.addressType"
                hide-eoa-icon
              />
            </template>
            <template #tokenInfo="{row}: {row: NftTradeModel}">
              <div class="flex w-full items-center gap-x-2">
                <BcLink
                  :content="row.tokenInfo.name ? `${row.tokenInfo.name}${row.tokenInfo.symbol && ` (${row.tokenInfo.symbol})`}` : row.tokenInfo.address "
                  :to="routes.getNftDetailPageWithTab(row.nftType, row.tokenInfo.address)"
                  class="w-full min-w-0 grow"
                >
                  <template #prefix>
                    <img
                      :src="row.tokenInfo.logo"
                      :alt="`${row.tokenInfo.name} symbol image`"
                      class="h-5 w-5 rounded-full"
                    >
                  </template>
                </BcLink>
              </div>
            </template>
            <template #item="{ row }: { row: NftTradeModel }">
              <div class="flex w-full items-center gap-x-2">
                <BcLink
                  :content="row.tokenName || '-'"
                  :to="routes.getNftItemDetailPageWithTab(row.nftType, row.tokenInfo.address, row.tokenId)"
                >
                  <template #prefix>
                    <img
                      :src="row.tokenImage || (IMAGE_BASE_URL + '/token/default.png')"
                      :alt="`${row.tokenName} image`"
                      class="h-5 w-5 rounded-full"
                    >
                  </template>
                </BcLink>
                <IconButton
                  icon="copy"
                  @click="copy(row.tokenId)"
                />
              </div>
            </template>
          </BcTable>
        </template>
      </CustomSuspense>
      <BcPagination
        v-model="pagination"
        :total-count="data?.viewCount"
        :row-per-page-list="rowPerPageList"
      />
    </Card>
  </div>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import { nftService } from '~/api/service/nft.service'
import DetailsBox from '~/components/box/DetailsBox.vue'
import IconButton from '~/components/button/IconButton.vue'
import PageTitleHeader from '~/components/header/PageTitleHeader.vue'
import AddressCopyLink from '~/components/link/AddressCopyLink.vue'
import TransactionCopyLink from '~/components/link/TransactionCopyLink.vue'
import NoDataPreview from '~/components/preview/NoDataPreview.vue'
import CustomSuspense from '~/components/suspense/CustomSuspense.vue'
import { useCopy } from '~/composables/useCopy'
import { useTableColumn } from '~/composables/useTable'
import { IMAGE_BASE_URL } from '~/constants/image.constant'
import { MARKETPLACE_URL, NFT_TRADES_COLUMN } from '~/constants/token.constants'
import { NftTradeModel } from '~/types/model/nft.model'
import BcLink from '~/ui/Link/BcLink.vue'
import BcPagination from '~/ui/Pagination/BcPagination.vue'
import BcTable from '~/ui/Table/BcTable.vue'

const route = useRoute()
const { copy } = useCopy()
const { pagination, rowPerPageList } = usePagination({
  page: 1,
  rowPerPage: 25
})

const { data, isLoading, isError } = nftService.useGetNftTradeList(pagination)

useSeoMeta(getMeta('NFT Trades', 'View the latest NFT trades'))

const path = route.fullPath.split('/').filter(elem => elem)
const localStorageKey = path.length > 1 ? `${path[0]}-${path[path.length - 1]}` : path[0]

const hideAge = ref()

if (process.client) {
  hideAge.value = JSON.parse(window.localStorage.getItem(localStorageKey) || 'false')
}

const exceptList = computed(() => {
  return hideAge.value ? [ 'age' ] : [ 'unixTimeStamp' ]
})

const toggle = () => {
  if (process.client) {
    hideAge.value = !hideAge.value
    window.localStorage.setItem(localStorageKey, JSON.stringify(hideAge.value))
  }
}
const { column } = useTableColumn(NFT_TRADES_COLUMN, exceptList)
</script>

<style scoped lang="scss">
.link {
  max-width: 200px;

  @include over_xl {
    max-width: 270px;
  }
}
</style>